import {
  leadSourceMap,
  type LeadSource,
} from '@mortgage-pos/data/leadSourcesMap'
import { LeadSourceType, ServicerLeadSource } from '@mortgage-pos/types'

/**
 *
 * @returns "name" in StudlyCaps/UpperCamelCase
 */
export function getBankrateLeadSource(
  offerTags: string[],
  isPrimaryLead?: boolean,
  landingUrl?: string,
  leadSource?: string
): LeadSource {
  if (leadSource?.toUpperCase().includes(LeadSourceType.Amazon)) {
    return leadSourceMap.AMAZON
  }
  if (leadSource?.toUpperCase().includes('WSJ')) {
    return leadSourceMap.BANKRATEWSJ
  }
  // BankrateEmail leads do not have a bankrateLeadId, so they get picked up via the generic url match logic
  // BankrateHomeEquity leads only have a bankRateLeadId, so also check via url here
  if (isPrimaryLead === true) {
    if (landingUrl?.includes(leadSourceMap.HOMEEQUITYPRIMARY.urlMatchString)) {
      return leadSourceMap.HOMEEQUITYPRIMARY
    }

    return leadSourceMap.BANKRATEPRIMARY
  }
  if (offerTags && offerTags.includes('digiFlex')) {
    return leadSourceMap.BANKRATEDIGIFLEX
  }

  if (landingUrl) {
    const landingUrlSource = getSourceByLandingUrl(String(landingUrl))
    if (
      landingUrlSource?.name &&
      ['Bankrate', 'HomeEquity'].some((s) => landingUrlSource?.name.includes(s))
    ) {
      return landingUrlSource
    }
  }

  // (isPrimaryLead === false) || NULL
  return leadSourceMap.BANKRATESECONDARY
}

export function getLeadSource(leadSourceName: string): LeadSource {
  // Match to map by key, trimmed & case-insensitive
  if (leadSourceName.trim().toUpperCase() in leadSourceMap) {
    return leadSourceMap[leadSourceName.trim().toUpperCase()]
  }
  return {
    name: leadSourceName,
  }
}

export function isBuilder(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  return source?.isBuilder === true
}

export function isEmployeeLoans(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  return source?.name?.toUpperCase() === LeadSourceType.EmployeeLoans
}

export function isRobinhood(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  return source?.name?.toUpperCase() === LeadSourceType.Robinhood
}

export function isRobinhoodConcierge(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  return source?.name?.toUpperCase() === LeadSourceType.RobinhoodConcierge
}

export function isRobinhoodSummit(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  return source?.name?.toUpperCase() === LeadSourceType.RobinhoodSummit
}

export function isAnyRobinhoodSource(leadSourceName: string): boolean {
  return (
    isRobinhood(leadSourceName) ||
    isRobinhoodConcierge(leadSourceName) ||
    isRobinhoodSummit(leadSourceName)
  )
}

export function isManualRoutingOnlySource(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  return getLeadSource(leadSourceName)?.manualRoutingOnly === true
}

export function isBestMoney(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  return source?.name?.toUpperCase() === LeadSourceType.BestMoney
}

export function isAnnalyLoanCare(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  const upperCaseName = source?.name?.toUpperCase()
  return (
    upperCaseName?.includes('LOANCARE') ||
    upperCaseName === LeadSourceType.AltairExperianRealtime ||
    upperCaseName === LeadSourceType.AltairPreMover
  )
}

export function isAnnalyLoanCarePayoff(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  return source?.name?.toUpperCase() === LeadSourceType.AnnalyLoanCarePayoff
}

export function isAnnalyLoanCareMoneyOutbound(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  return (
    source?.name?.toUpperCase() === LeadSourceType.AnnalyLoanCareMoneyOutbound
  )
}

/**
 * Get the lead source name associated with a servicer lead source.
 */
export function getLeadSourceByServicer(
  servicerSource: ServicerLeadSource
): string | undefined {
  for (const key in leadSourceMap) {
    if (leadSourceMap[key].servicer === servicerSource) {
      return leadSourceMap[key].name
    }
  }
  return undefined
}

/**
 * Servicer leads arrive through the prefill apply link, so their application is
 * created before the borrower's browser is ever involved.
 */
export function isServicerLeadSource(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  return Boolean(getLeadSource(leadSourceName)?.servicer)
}

export function getSource(sourceData: SourceData) {
  // Match to map by the source data containing a field with a name matching "sourceField"
  for (const key in leadSourceMap) {
    if (sourceData[leadSourceMap[key].sourceField]) {
      return leadSourceMap[key].name
    }
  }

  return leadSourceMap[LeadSourceType.SageWebsite].name
}

export function getSourceByLandingUrl(landingUrl: string) {
  const landingUrlLowerCase = landingUrl.toLowerCase()

  const strictMatches = Object.keys(leadSourceMap).reduce((acc, key) => {
    if (typeof leadSourceMap[key].urlMatchParams !== 'undefined') {
      acc.push(key)
    }
    return acc
  }, [])

  // Sort by the number of parameters in the urlMatchParams array
  // in order of most parameters to least to match on stricter sources first
  // (i.e. RVMN has multiple related sources and is treated as the fall back)
  strictMatches.sort((a, b) => {
    const aLength = leadSourceMap[a].urlMatchParams?.length || 0
    const bLength = leadSourceMap[b].urlMatchParams?.length || 0
    return bLength - aLength
  })

  // Check for multiple parameter matches first (case insensitive)
  for (const key of strictMatches) {
    if (
      leadSourceMap[key].urlMatchParams.every((param) =>
        landingUrlLowerCase.includes(param.toLowerCase())
      )
    ) {
      return leadSourceMap[key]
    }
  }

  // Collect all urlMatchString matches and sort by string length (descending) to prioritize more specific matches (ex: "src=encoreincentive" over "src=encore")
  // Matching is case insensitive
  const urlStringMatches = []
  for (const key in leadSourceMap) {
    if (
      typeof leadSourceMap[key].urlMatchString !== 'undefined' &&
      landingUrlLowerCase.includes(
        leadSourceMap[key].urlMatchString.toLowerCase()
      )
    ) {
      urlStringMatches.push({
        key,
        matchString: leadSourceMap[key].urlMatchString,
      })
    }
  }

  urlStringMatches.sort((a, b) => b.matchString.length - a.matchString.length)

  if (urlStringMatches.length > 0) {
    return leadSourceMap[urlStringMatches[0].key]
  }

  // Check secondary match strings (case insensitive)
  for (const key in leadSourceMap) {
    if (
      typeof leadSourceMap[key].urlSecondaryMatchString !== 'undefined' &&
      landingUrlLowerCase.includes(
        leadSourceMap[key].urlSecondaryMatchString.toLowerCase()
      )
    ) {
      return leadSourceMap[key]
    }
  }
}

type SourceData = {
  fBLeadId?
  bankrateLeadId?
  gclid?
}

export const leadSourcesWithConversionTracking = [
  LeadSourceType.RateZip,
  LeadSourceType.TheMortgageReport,
  LeadSourceType.MilitaryVALoan,
] as const

export const leadSourceNamesWithConversionTracking =
  leadSourcesWithConversionTracking.map((source) => getLeadSource(source).name)
